import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { Toaster } from './components/Toaster';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import Transactions from './pages/Transactions';
import Settings from './pages/Settings';
import Login from './pages/Login';
import LinkTelegram from './pages/LinkTelegram';
import AssetDetail from './pages/AssetDetail';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';

/** Watches user.settings.theme and adds/removes the `dark` class on <html>. */
function ThemeWatcher() {
  const { user } = useAuth();
  useEffect(() => {
    const html = document.documentElement;
    const stored = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;
    const theme = user?.settings.theme ?? stored ?? 'system';
    if (theme === 'dark') {
      html.classList.add('dark');
      return;
    }
    if (theme === 'light') {
      html.classList.remove('dark');
      return;
    }
    // system — follow OS preference and update on change
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.matches ? html.classList.add('dark') : html.classList.remove('dark');
    const handler = (e: MediaQueryListEvent) =>
      e.matches ? html.classList.add('dark') : html.classList.remove('dark');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [user?.settings.theme]);
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ToastProvider>
    <BrowserRouter>
      <ThemeWatcher />
      <Toaster />
      <Routes>
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        {/* Public – but redirects to login if not authenticated */}
        <Route path="/link-telegram" element={<LinkTelegram />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="assets" element={<Assets />} />
          <Route path="assets/:id" element={<AssetDetail />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="settings" element={<Settings />} />
          <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="admin/users/:userId" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ToastProvider>
  );
}
